import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { apiClient } from '@/api/client';
import { cn } from '@/utils/cn';
import type { ApiResponse, PagedResult } from '@/types/api.types';

interface AccountOption {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

interface AccountListItem {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

interface AccountSearchInputProps {
  /** Currently selected account id */
  accountId?: string;
  /** Currently selected account display label (if provided, used as initial display) */
  accountLabel?: string;
  /** Called when user selects an account */
  onChange: (accountId: string, code: string, name: string) => void;
  /** Called when user clears the selection */
  onClear?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const AccountSearchInput = ({
  accountId,
  accountLabel,
  onChange,
  onClear,
  placeholder = 'Search account code or name…',
  required,
  disabled,
  className,
}: AccountSearchInputProps) => {
  const [query, setQuery] = useState(accountLabel ?? '');
  const [options, setOptions] = useState<AccountOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AccountOption | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // If accountId+accountLabel are pre-filled (from URL params), show the label
  useEffect(() => {
    if (accountId && accountLabel && !selected) {
      setQuery(accountLabel);
    }
  }, [accountId, accountLabel, selected]);

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 1) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get<ApiResponse<PagedResult<AccountListItem>>>(
        '/accounts',
        { params: { search: q, pageSize: 20, pageNumber: 1 } }
      );
      const items = res.data?.data?.items ?? [];
      setOptions(items.map(i => ({
        id: i.id,
        code: i.code,
        name: i.name,
        accountType: i.accountType,
      })));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = (opt: AccountOption) => {
    setSelected(opt);
    setQuery(`${opt.code} — ${opt.name}`);
    setOpen(false);
    onChange(opt.id, opt.code, opt.name);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setOptions([]);
    setOpen(false);
    onClear?.();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        listRef.current && !listRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (query && !selected) { setOpen(true); search(query); } }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={cn(
            'w-full pl-8 pr-8 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm',
            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
            'focus:outline-none focus:ring-2 focus:ring-brand-500',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        />
        {selected || query ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500 text-center">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500 text-center">
              {query.length < 1 ? 'Start typing to search accounts' : 'No accounts found'}
            </div>
          ) : (
            options.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelect(opt)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0"
              >
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">{opt.code}</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{opt.name}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{opt.accountType}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
